import React, { useEffect, useState } from 'react'
import { FaApple } from 'react-icons/fa6'
import { FcGoogle } from 'react-icons/fc'

type Providers = { google: boolean; apple: boolean }

/**
 * Where this app is served from: its origin plus its base path (VITE_BASE), e.g.
 * https://dev.lunaparkdigital.com/athlete. On hosted dev every app shares one host and lives
 * under a path, so the backend needs this to send the browser back to the right app after the
 * provider. It checks the value against the instance's known app URLs.
 */
export const appBaseUrl = () =>
  `${window.location.origin}${((import.meta as any).env?.VITE_BASE ?? '/').replace(/\/+$/, '')}`

/**
 * "Sign in with Google" and "Sign in with Apple". Each button sends the browser to the backend,
 * which hands off to the provider and brings the person back signed in (see
 * apps/adthlete-backend/services/user/oauthSignInService.ts). A provider the server hasn't been
 * set up for yet shows as coming soon.
 */
export const SocialSignInButtons: React.FC<{
  /** The account type this app signs in: 'marketer', 'athlete', ... */
  userType: string
  /** A path in this app to land on afterwards */
  returnTo?: string | null
  /** Called when someone taps a provider that isn't set up yet */
  onUnavailable?: (provider: 'Google' | 'Apple') => void
  className?: string
  buttonClassName?: string
}> = ({ userType, returnTo, onUnavailable, className, buttonClassName }) => {
  const [providers, setProviders] = useState<Providers | null>(null)

  useEffect(() => {
    fetch('/api/users/oauth/providers')
      .then((r) => (r.ok ? r.json() : { google: false, apple: false }))
      .then(setProviders)
      .catch(() => setProviders({ google: false, apple: false }))
  }, [])

  const go = async (provider: 'google' | 'apple') => {
    // The list was fetched when the page loaded; a provider may have been set up since, so
    // check again before telling someone it isn't available
    let enabled = providers
    if (!enabled?.[provider]) {
      enabled = await fetch('/api/users/oauth/providers')
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
      if (enabled) setProviders(enabled)
    }
    if (!enabled?.[provider]) {
      onUnavailable?.(provider === 'google' ? 'Google' : 'Apple')
      return
    }
    const params = new URLSearchParams({
      user_type: userType,
      origin: window.location.origin,
      base: appBaseUrl(),
      ...(returnTo ? { returnTo } : {}),
    })
    window.location.assign(`/api/users/oauth/${provider}/start?${params}`)
  }

  const base =
    buttonClassName ??
    'flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] text-[15px] font-medium text-[#111827] transition-colors hover:bg-[#F9FAFB]'
  const soon = (on: boolean | undefined) =>
    providers && !on ? (
      <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]">
        Soon
      </span>
    ) : null

  return (
    <div className={className ?? 'space-y-3'}>
      <button type="button" className={base} onClick={() => go('google')}>
        <FcGoogle className="h-5 w-5" /> Sign in with Google {soon(providers?.google)}
      </button>
      {/* Apple isn't offered until its Services ID is configured (APPLE_SIGNIN_CLIENT_ID) */}
      {providers?.apple && (
        <button type="button" className={base} onClick={() => go('apple')}>
          <FaApple className="h-5 w-5 text-[#000000]" /> Sign in with Apple
        </button>
      )}
    </div>
  )
}

export default SocialSignInButtons
